import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { overrideDesignTokens } from '../../shared/styles/override-design-tokens';
import { ludsBaseFont } from '../../shared/styles/typography';
import '../icon/icon';
import { ToggleEvents } from './toggle.model';

/** Indicates whether to show the information icon/button next to the toggle. */
@customElement('luds-toggle')
export class LudsToggle extends LitElement {
  /** Indicates whether the toggle is checked or not. */
  @property({ type: Boolean, reflect: true })
  checked = false;

  /** Indicates whether the toggle is disabled. */
  @property({ type: Boolean, reflect: true })
  disabled = false;

  /** Indicates whether to display the information icon alongside the toggle. */
  @property({ type: Boolean, reflect: true })
  showInfo = false;

  /** A11Y - Aria label for the information icon. */
  @property({ type: String, reflect: true })
  showInfoAriaLabel: string;

  static override styles = [
    overrideDesignTokens,
    ludsBaseFont,
    css`
      :host {
        --luds-toggle-checked-color: var(--semantic-progress);
        --luds-toggle-disabled-color: var(--object-object-disabled);
        --luds-toggle-background-color: var(--object-object-disabled);
        --luds-toggle-handle-background-color: var(--surfaces-surface-light);
        --luds-toggle-handle-box-shadow: 2px solid var(--colours-neutrals-white-negative);
        --luds-toggle-handle-spacing: var(--spacing-spacing-02);
        --luds-toggle-switch-spacing: var(--spacing-spacing-08);
        --luds-toggle-height: 20px;
        --luds-toggle-width: 36px;
        --luds-toggle-handle-size: 16px;

        box-sizing: border-box;
        display: flex;
        align-items: center;
        gap: var(--luds-toggle-switch-spacing);
      }

      .toggle {
        display: inline-flex;
        align-items: center;
        cursor: pointer;
        opacity: 1;
        transition: opacity 0.3s;
      }

      .toggle-disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .toggle-icon {
        display: flex;
        align-items: center;
      }

      .switch {
        position: relative;
        display: inline-block;
        width: var(--luds-toggle-width);
        height: var(--luds-toggle-height);
        margin-right: var(--luds-toggle-switch-spacing);
      }

      .switch input {
        opacity: 0;
        width: 0;
        height: 0;
      }

      .handle {
        position: absolute;
        cursor: pointer;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: var(--luds-toggle-background-color);
        transition: background 0.4s;
        border-radius: var(--luds-toggle-height);
      }

      .handle:before {
        position: absolute;
        content: '';
        height: var(--luds-toggle-handle-size);
        width: var(--luds-toggle-handle-size);
        left: var(--luds-toggle-handle-spacing);
        bottom: var(--luds-toggle-handle-spacing);
        background-color: var(--luds-toggle-handle-background-color);
        transition: 0.4s;
        border-radius: 50%;
      }

      input:checked + .handle {
        background-color: var(--luds-toggle-checked-color);
      }

      input:checked + .handle:before {
        transform: translateX(calc((var(--luds-toggle-width)) - var(--luds-toggle-height)));
      }

      input:disabled + .handle {
        background-color: var(--luds-toggle-disabled-color);
        cursor: not-allowed;
      }

      input:disabled + .handle:before {
        background-color: var(--luds-toggle-handle-background-color);
      }

      :host([disabled]) ::slotted(*) {
        color: var(--luds-toggle-disabled-color);
        cursor: not-allowed;
      }

      input:focus-visible + .handle {
        outline: none;
        box-shadow:
          0 0 0 2px var(--colours-neutrals-white-negative),
          0 0 0 4px var(--semantic-colour-tokens-stroke-stroke-brand),
          0 0 8px 1px var(--semantic-colour-tokens-stroke-stroke-brand);
      }

      ::slotted(*) {
        white-space: nowrap;
      }
    `,
  ];

  private handleToggleChange() {
    if (this.disabled) return;

    this.checked = !this.checked;
    const event = new Event(ToggleEvents.CHANGED);

    this.dispatchEvent(event);
  }

  private handleToggleInfoClick(event: Event) {
    if (this.disabled) return;

    event.preventDefault();

    const infoIconEvent = new Event(ToggleEvents.INFO_CLICKED, {
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(infoIconEvent);
  }

  override render() {
    const { checked, disabled, handleToggleChange, showInfo, handleToggleInfoClick, showInfoAriaLabel } = this;

    return html`
      <label class="toggle ${disabled ? 'toggle-disabled' : ''}">
        <span class="switch">
          <input
            type="checkbox"
            role="switch"
            .checked=${checked}
            .disabled=${disabled}
            @change=${handleToggleChange}
            aria-checked=${checked ? 'true' : 'false'}
            aria-disabled=${disabled ? 'true' : 'false'} />
          <span class="handle"></span>
        </span>
        <slot></slot>
      </label>
      ${showInfo
        ? html`<a
            href="#"
            aria-label=${showInfoAriaLabel}
            @click=${handleToggleInfoClick}
            class="toggle-icon ${disabled ? 'toggle-disabled' : ''}">
            <luds-icon icon="info" size="extra-small" aria-hidden="true"></luds-icon>
          </a>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'luds-toggle': LudsToggle;
  }
}
